import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextareaInput } from "@/components/ui/Field";
import { createDokumentBillingQuestions } from "@/lib/dokumentBilling";
import { toast } from "@/lib/toast";
import {
  normalizePositionTyp,
  positionDisplayTitle,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";

export function DocumentBillingQuestionCreateDialog({
  open,
  onClose,
  dokumentId,
  positions,
  zIndexClass,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  dokumentId: number;
  positions: EditorPosition[];
  zIndexClass?: string;
  onCreated: () => void;
}) {
  const selectable = useMemo(
    () =>
      positions.filter((p) => {
        const typ = normalizePositionTyp(p.position_typ);
        return typ !== "titel" && typ !== "text" && p.id != null;
      }),
    [positions],
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [positionTexts, setPositionTexts] = useState<Record<number, string>>({});
  const [generalQuestion, setGeneralQuestion] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setSelectedIds(new Set());
    setPositionTexts({});
    setGeneralQuestion("");
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    const position_questions = [...selectedIds]
      .map((position_id) => ({
        position_id,
        question_text: (positionTexts[position_id] || "").trim(),
      }))
      .filter((row) => row.question_text);

    const general = generalQuestion.trim();
    if (!general && position_questions.length === 0) {
      toast.error("Bitte mindestens eine Rückfrage eingeben.");
      return;
    }

    setBusy(true);
    try {
      await createDokumentBillingQuestions(dokumentId, {
        general_question: general || undefined,
        position_questions,
      });
      toast.success("Rückfrage gesendet.");
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Rückfrage fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Rückfrage stellen"
      description="Wähle Positionen aus und formuliere die Fragen an die Geschäftsführung."
      size="xl"
      zIndexClass={zIndexClass}
      fullScreenMobile
      footer={
        <>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={busy} loading={busy}>
            Rückfrage senden
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextareaInput
          label="Allgemeine Rückfrage (optional)"
          value={generalQuestion}
          onChange={(e) => setGeneralQuestion(e.target.value)}
          rows={3}
          placeholder="Allgemeine Frage zum Dokument…"
        />

        {selectable.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Keine gespeicherten Positionen vorhanden. Bitte zuerst speichern.
          </p>
        ) : (
          <ul className="space-y-2" role="list">
            {selectable.map((pos) => {
              const id = Number(pos.id);
              const checked = selectedIds.has(id);
              return (
                <li
                  key={pos.localId}
                  className="rounded-ios border border-separator bg-fill-quaternary/20 px-3 py-2.5"
                >
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={checked}
                      onChange={() => toggle(id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-label">
                        {positionDisplayTitle(pos)}
                      </span>
                      {checked ? (
                        <TextareaInput
                          className="mt-2"
                          label="Frage zu dieser Position"
                          value={positionTexts[id] || ""}
                          onChange={(e) =>
                            setPositionTexts((prev) => ({ ...prev, [id]: e.target.value }))
                          }
                          rows={2}
                          placeholder="Rückfrage…"
                        />
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
